import {useEffect, useMemo, useRef, useState} from "react";
import axios, {CancelTokenSource} from "axios";
import {ApiResponseDto, DropDownListItem, NameWithId} from "@utils/Models";
import {RecipeDescriptionGeneratorDataModel} from "@pages/orders/order.models";
import {ApiUrl_Orders_GetRecipeDescriptionGeneratorData} from "@utils/Endpoints";
import {useFlintNotificationContext} from "@components/page_components/common/notifications/FlintNotificationContext";
import {GUID} from "@utils/AppConstants";
import {RecipeDescriptionGeneratorPanelProps} from "@pages/orders/preview/recipes/edit/description_generator/RecipeDescriptionGeneratorPanel";

const MAX_LENGTH = 255;

export interface SubstrateOption extends DropDownListItem {
    Symbol: string;
}

// Session-scoped cache: the picker data is stable per order for the duration of
// an edit session (per BE handoff). Cache survives popup unmount but resets on
// full page reload.
const sessionCache = new Map<GUID, RecipeDescriptionGeneratorDataModel>();

export function useRecipeDescriptionGeneratorPanel(props: RecipeDescriptionGeneratorPanelProps) {
    const {showNotification} = useFlintNotificationContext();

    const cached = sessionCache.get(props.OrderGuid);
    const [data, setData] = useState<RecipeDescriptionGeneratorDataModel | undefined>(cached);
    const [isLoading, setIsLoading] = useState<boolean>(!cached);

    const cancelTokenRef = useRef<CancelTokenSource | null>(null);

    useEffect(() => {
        if (sessionCache.has(props.OrderGuid)) {
            // already populated from synchronous useState init — nothing to fetch.
            return;
        }
        cancelTokenRef.current = axios.CancelToken.source();
        setIsLoading(true);
        axios.get<ApiResponseDto<RecipeDescriptionGeneratorDataModel>>(
            ApiUrl_Orders_GetRecipeDescriptionGeneratorData,
            {params: {orderGuid: props.OrderGuid}, cancelToken: cancelTokenRef.current.token}
        )
            .then(res => {
                if (res.data.Result) {
                    sessionCache.set(props.OrderGuid, res.data.Result);
                    setData(res.data.Result);
                }
            })
            .catch(err => {
                if (!axios.isCancel(err)) showNotification(err);
            })
            .finally(() => setIsLoading(false));

        return () => cancelTokenRef.current?.cancel("Description generator popup closed");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.OrderGuid]);

    const inkSeriesOptions = useMemo<NameWithId[]>(() => {
        return (data?.AvailableInkSeriesList ?? []).map(s => ({
            Id: s.Id,
            Name: s.Name,
        } as NameWithId));
    }, [data]);

    const substrateOptions = useMemo<SubstrateOption[]>(() => {
        const values = data?.AvailableSubstratesList?.FieldValues ?? [];
        return values.map(v => ({
            Id: v.ValueId,
            Name: v.Translation || v.FieldValue,
            Symbol: v.Symbol,
        }));
    }, [data]);

    const initialInkSerie = useMemo<NameWithId | undefined>(() => {
        if (!props.InitialInkSerieId) return undefined;
        return inkSeriesOptions.find(s => s.Id === props.InitialInkSerieId);
    }, [props.InitialInkSerieId, inkSeriesOptions]);

    const initialSubstrate = useMemo<SubstrateOption | undefined>(() => {
        if (!props.InitialSubstrateId) return undefined;
        return substrateOptions.find(v => v.Id === props.InitialSubstrateId);
    }, [props.InitialSubstrateId, substrateOptions]);

    const [selectedInkSerie, setSelectedInkSerie] = useState<NameWithId | undefined>(undefined);
    const [selectedSubstrate, setSelectedSubstrate] = useState<SubstrateOption | undefined>(undefined);
    const [colourStandardName, setColourStandardName] = useState<string>(props.InitialColourStandardName ?? "");

    // Sync default selection once lists arrive (or change due to fresh fetch).
    useEffect(() => {
        if (initialInkSerie && !selectedInkSerie) setSelectedInkSerie(initialInkSerie);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialInkSerie]);
    useEffect(() => {
        if (initialSubstrate && !selectedSubstrate) setSelectedSubstrate(initialSubstrate);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialSubstrate]);

    const previewParts = useMemo(() => ({
        inkSerie: selectedInkSerie?.Name?.trim() ?? "",
        colourStandardName: colourStandardName?.trim() ?? "",
        substrateSymbol: selectedSubstrate?.Symbol?.trim() ?? "",
    }), [selectedInkSerie, colourStandardName, selectedSubstrate]);

    const rawPreview = useMemo(() => {
        return [previewParts.inkSerie, previewParts.colourStandardName, previewParts.substrateSymbol]
            .filter(p => p.length > 0)
            .join(" ");
    }, [previewParts]);

    const willTruncate = rawPreview.length > MAX_LENGTH;
    const acceptValue = willTruncate ? rawPreview.slice(0, MAX_LENGTH) : rawPreview;

    function handleInkSerieChanged(value: NameWithId) {
        setSelectedInkSerie(value);
    }

    function handleSubstrateChanged(value: SubstrateOption) {
        setSelectedSubstrate(value);
    }

    function handleColourStandardNameChanged(value: string) {
        setColourStandardName(value);
    }

    function handleAccept() {
        props.OnAccepted(acceptValue);
    }

    function handleCancel() {
        props.OnCancelled();
    }

    return {
        state: {
            inkSerieOptions: {get: inkSeriesOptions},
            substrateOptions: {get: substrateOptions},
            selectedInkSerie: {get: selectedInkSerie},
            selectedSubstrate: {get: selectedSubstrate},
            colourStandardName: {get: colourStandardName},
            preview: {get: rawPreview},
            previewParts: {get: previewParts},
            acceptValue: {get: acceptValue},
            isLoading: {get: isLoading},
        },
        handlers: {
            handleInkSerieChanged,
            handleSubstrateChanged,
            handleColourStandardNameChanged,
            handleAccept,
            handleCancel,
        },
        utilities: {
            willTruncate,
            maxLength: MAX_LENGTH,
        },
    };
}
